import { useCallback } from 'react'

import { useAuthInfo } from '@propelauth/react'
import {
  type QueryClient,
  type QueryKey,
  UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { produce } from 'immer'
import _ from 'lodash'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import { UUID } from '@/types/Id'
import { OpportunityType } from '@/types/Opportunity'
import {
  AgentName,
  FormChatSession,
  KnownModelName,
  OpportunityChatSession,
  OrganizationChatSession,
  PursuitChatSession,
  PursuitDocumentChatSession,
  SpreadsheetChatSession,
} from '@/types/chat-v2'

const DEFAULT_MODEL: KnownModelName = 'anthropic:claude-sonnet-4-5-20250929'

export interface ChatSessionUpdate {
  title?: string | null
  shared_with_user_ids?: UUID[]
  current_run_id?: UUID
}

export interface BulkChatSessionsDelete {
  ids: UUID[]
}

export interface ChatSessionListItem {
  id: UUID
  created_at: string
  created_by: UUID
  last_modified: string
  initial_agent_name: AgentName
  shared_with_user_ids: UUID[]
  title: string | null
}

export interface ChatSessionListResponse {
  sessions: ChatSessionListItem[]
  count: number
  has_more: boolean
}

export interface ChatSessionQueryKeyArgs {
  agentName: AgentName
  sessionId: UUID | ''
  orgId: UUID
}

export const chatSessionQueryKeys = {
  session: (args: ChatSessionQueryKeyArgs) =>
    ['chat-session', args.orgId, args.agentName, args.sessionId] as const,
  all: () => ['chat-session'] as const,
}

// region: Session List Fetch Functions

interface OrganizationChatSessionListQueryKeyArgs {
  agentName: 'organization_chat'
  orgId: UUID
}

interface OpportunityChatSessionListQueryKeyArgs {
  agentName: 'opportunity_chat'
  orgId: UUID
}

interface PursuitChatSessionListQueryKeyArgs {
  agentName: 'pursuit_chat'
  pursuitId: UUID
}

interface PursuitDocumentChatSessionListQueryKeyArgs {
  agentName: 'pursuit_document_chat'
  pursuitId: UUID
  documentId: UUID
}

interface FormChatSessionListQueryKeyArgs {
  agentName: 'form_chat'
  orgId: UUID
  formId?: UUID
}

interface SpreadsheetChatSessionListQueryKeyArgs {
  agentName: 'spreadsheet_chat'
  orgId: UUID
  spreadsheetId?: UUID
}

export type ChatSessionListQueryKeyArgs =
  | OrganizationChatSessionListQueryKeyArgs
  | OpportunityChatSessionListQueryKeyArgs
  | PursuitChatSessionListQueryKeyArgs
  | PursuitDocumentChatSessionListQueryKeyArgs
  | FormChatSessionListQueryKeyArgs
  | SpreadsheetChatSessionListQueryKeyArgs

interface FetchOpportunityChatSessionListArgs {
  orgId: UUID
}

const fetchOpportunityChatSessionList = async (
  args: FetchOpportunityChatSessionListArgs,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/opportunity/sessions?organization_id=${args.orgId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

interface FetchOrganizationChatSessionListArgs {
  orgId: UUID
}

const fetchOrganizationChatSessionList = async (
  args: FetchOrganizationChatSessionListArgs,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/organization/${args.orgId}/sessions`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

interface FetchPursuitChatSessionListArgs {
  pursuitId: UUID
}

interface FetchFormChatSessionListArgs {
  orgId: UUID
  formId?: UUID
}

const fetchPursuitChatSessionList = async (
  args: FetchPursuitChatSessionListArgs,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/pursuit/sessions?pursuit_id=${args.pursuitId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

interface FetchPursuitDocumentChatSessionListArgs {
  pursuitId: UUID
  documentId: UUID
}

const fetchPursuitDocumentChatSessionList = async (
  args: FetchPursuitDocumentChatSessionListArgs,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions?pursuit_id=${args.pursuitId}&document_id=${args.documentId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

const fetchFormChatSessionList = async (
  args: FetchFormChatSessionListArgs,
  accessToken: string,
) => {
  const urlSearchParams = new URLSearchParams()
  urlSearchParams.set('organization_id', args.orgId)
  if (args.formId) {
    urlSearchParams.set('form_id', args.formId)
  }
  const url = `${API_URL}/api/v1/chat/form/sessions?${urlSearchParams.toString()}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

interface FetchSpreadsheetChatSessionListArgs {
  orgId: UUID
  spreadsheetId?: UUID
}

const fetchSpreadsheetChatSessionList = async (
  args: FetchSpreadsheetChatSessionListArgs,
  accessToken: string,
) => {
  const urlSearchParams = new URLSearchParams()
  urlSearchParams.set('organization_id', args.orgId)
  if (args.spreadsheetId) {
    urlSearchParams.set('spreadsheet_id', args.spreadsheetId)
  }
  const url = `${API_URL}/api/v1/chat/spreadsheet/sessions?${urlSearchParams.toString()}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListResponse
}

const fetchChatSessionList = async (
  args: ChatSessionListQueryKeyArgs,
  accessToken: string,
) => {
  switch (args.agentName) {
    case 'opportunity_chat':
      return fetchOpportunityChatSessionList(args, accessToken)
    case 'organization_chat':
      return fetchOrganizationChatSessionList(args, accessToken)
    case 'pursuit_chat':
      return fetchPursuitChatSessionList(args, accessToken)
    case 'pursuit_document_chat':
      return fetchPursuitDocumentChatSessionList(args, accessToken)
    case 'form_chat':
      return fetchFormChatSessionList(args, accessToken)
    case 'spreadsheet_chat':
      return fetchSpreadsheetChatSessionList(args, accessToken)
    default:
      const exhaustiveCheck: never = args
      return exhaustiveCheck
  }
}

// endregion: Session List Fetch Functions

// region: Opportunity Chats

interface UseOpportunityChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  select?: (data: OpportunityChatSession) => T
}

export const useOpportunityChatSession = <T = OpportunityChatSession>(
  args: UseOpportunityChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: OpportunityChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'opportunity_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: () => fetchOpportunityChatSession(args, accessToken),
    select: selectFn,
    enabled: !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreateOpportunityChatSessionBody {
  organization_id: UUID
  opportunity_id: UUID
  opportunity_type: OpportunityType
  title?: string | null
  model_name?: KnownModelName
}

export const useCreateOpportunityChatSession = () => {
  // use regular auth info here since the user might not be logged in
  // when this hook is used
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (args: CreateOpportunityChatSessionBody) =>
      createOpportunityChatSession(args, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'opportunity_chat',
          orgId: variables.organization_id,
          sessionId: data.id,
        }),
        data,
      )

      const simpleChatSessionListItem = chatSessionToListItem(data)
      queryClient.setQueryData(
        simpleChatSessionQueryKeys.session({
          sessionId: data.id,
        }),
        simpleChatSessionListItem,
      )

      // Invalidate the specific agent query
      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'opportunity_chat',
          orgId: variables.organization_id,
        }),
      })
    },
  })
}

interface UseUpdateOpportunityChatSessionArgs {
  orgId: UUID
  sessionId: UUID
}

export const useUpdateOpportunityChatSession = (
  args: UseUpdateOpportunityChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updateOpportunityChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'opportunity_chat',
          orgId: args.orgId,
          sessionId: args.sessionId,
        }),
      })

      queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.organization({
          orgId: args.orgId,
        }),
      })

      // Invalidate the specific agent query
      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'opportunity_chat',
          orgId: args.orgId,
        }),
      })
    },
  })
}

export const chatSessionListQueryKeys = {
  all: () => ['chat-session-list'] as const,
  agent: (args: ChatSessionListQueryKeyArgs) => {
    switch (args.agentName) {
      case 'organization_chat':
        return chatSessionListQueryKeys.organization(args)
      case 'opportunity_chat':
        return chatSessionListQueryKeys.opportunity(args)
      case 'pursuit_chat':
        return chatSessionListQueryKeys.pursuit(args)
      case 'pursuit_document_chat':
        return chatSessionListQueryKeys.pursuitDocument(args)
      case 'form_chat':
        return chatSessionListQueryKeys.form(args)
      case 'spreadsheet_chat':
        return chatSessionListQueryKeys.spreadsheet(args)
      default:
        const exhaustiveCheck: never = args
        return exhaustiveCheck
    }
  },
  opportunity: (
    args: Omit<OpportunityChatSessionListQueryKeyArgs, 'agentName'>,
  ) => ['chat-session-list', 'opportunity_chat', args.orgId] as const,
  organization: (
    args: Omit<OrganizationChatSessionListQueryKeyArgs, 'agentName'>,
  ) => ['chat-session-list', 'organization_chat', args.orgId] as const,
  pursuit: (args: Omit<PursuitChatSessionListQueryKeyArgs, 'agentName'>) =>
    ['chat-session-list', 'pursuit_chat', args.pursuitId] as const,
  pursuitDocument: (
    args: Omit<PursuitDocumentChatSessionListQueryKeyArgs, 'agentName'>,
  ) =>
    [
      'chat-session-list',
      'pursuit_document_chat',
      args.pursuitId,
      args.documentId,
    ] as const,
  form: (args: Omit<FormChatSessionListQueryKeyArgs, 'agentName'>) => {
    if (args.formId) {
      return [
        'chat-session-list',
        'form_chat',
        args.orgId,
        args.formId,
      ] as const
    }
    return ['chat-session-list', 'form_chat', args.orgId]
  },
  spreadsheet: (
    args: Omit<SpreadsheetChatSessionListQueryKeyArgs, 'agentName'>,
  ) => {
    if (args.spreadsheetId) {
      return [
        'chat-session-list',
        'spreadsheet_chat',
        args.orgId,
        args.spreadsheetId,
      ] as const
    }
    return ['chat-session-list', 'spreadsheet_chat', args.orgId]
  },
}

interface UseOpportunityChatSessionListArgs {
  orgId: UUID
}

export const useOpportunityChatSessionList = (
  args: UseOpportunityChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.organization({ orgId: args.orgId }),
    queryFn: () => fetchOpportunityChatSessionList(args, accessToken),
    staleTime: Infinity,
  })
}

interface FetchOpportunityChatSessionArgs {
  sessionId: UUID | ''
}

const fetchOpportunityChatSession = async (
  args: FetchOpportunityChatSessionArgs,
  accessToken: string,
) => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/opportunity/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as OpportunityChatSession
}

export const createOpportunityChatSession = async (
  args: CreateOpportunityChatSessionBody,
  accessToken: string | null | undefined,
) => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/opportunity/sessions`

  if (!args.model_name) {
    args.model_name = DEFAULT_MODEL
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(args),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as OpportunityChatSession
}

const updateOpportunityChatSession = async (
  args: UseUpdateOpportunityChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/opportunity/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Opportunity Chats

// region: Organization Chats

interface UseOrganizationChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  select?: (data: OrganizationChatSession) => T
}

export const useOrganizationChatSession = <T = OrganizationChatSession>(
  args: UseOrganizationChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: OrganizationChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'organization_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: () => fetchOrganizationChatSession(args, accessToken),
    select: selectFn,
    enabled: !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreateOrganizationChatSessionBody {
  organization_id: UUID
  model_name?: KnownModelName
  title?: string | null
}

export const useCreateOrganizationChatSession = () => {
  // use regular auth info here since the user might not be logged in
  // when this hook is used
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (args: CreateOrganizationChatSessionBody) =>
      createOrganizationChatSession(args, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'organization_chat',
          orgId: variables.organization_id,
          sessionId: data.id,
        }),
        data,
      )

      const simpleChatSessionListItem = chatSessionToListItem(data)
      queryClient.setQueryData(
        simpleChatSessionQueryKeys.session({
          sessionId: data.id,
        }),
        simpleChatSessionListItem,
      )

      // Invalidate the specific agent query
      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'organization_chat',
          orgId: variables.organization_id,
        }),
      })
    },
  })
}

interface UseUpdateOrganizationChatSessionArgs {
  organizationId: UUID
  sessionId: UUID
}

export const useUpdateOrganizationChatSession = (
  args: UseUpdateOrganizationChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updateOrganizationChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'organization_chat',
          orgId: args.organizationId,
          sessionId: args.sessionId,
        }),
      })

      queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.organization({
          orgId: args.organizationId,
        }),
      })

      // Invalidate the specific agent query
      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'organization_chat',
          orgId: args.organizationId,
        }),
      })
    },
  })
}

interface UseOrganizationChatSessionListArgs {
  organizationId: UUID
}

export const useOrganizationChatSessionList = (
  args: UseOrganizationChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.agent({
      agentName: 'organization_chat',
      orgId: args.organizationId,
    }),
    queryFn: () =>
      fetchOrganizationChatSessionList(
        { orgId: args.organizationId },
        accessToken,
      ),
    staleTime: Infinity,
  })
}

interface FetchOrganizationChatSessionArgs {
  orgId: UUID
  sessionId: UUID | ''
}

const fetchOrganizationChatSession = async (
  args: FetchOrganizationChatSessionArgs,
  accessToken: string,
): Promise<OrganizationChatSession> => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/organization/${args.orgId}/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as OrganizationChatSession
}

export const createOrganizationChatSession = async (
  args: CreateOrganizationChatSessionBody,
  accessToken: string | null | undefined,
) => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/organization/${args.organization_id}/sessions`

  if (!args.model_name) {
    args.model_name = DEFAULT_MODEL
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(args),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as OrganizationChatSession
}

const updateOrganizationChatSession = async (
  args: UseUpdateOrganizationChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/organization/${args.organizationId}/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Organization Chats

// region: Pursuit Chats

interface UsePursuitChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  enabled?: boolean
  select?: (data: PursuitChatSession) => T
}

export const usePursuitChatSession = <T = PursuitChatSession>(
  args: UsePursuitChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PursuitChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'pursuit_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: () => fetchPursuitChatSession(args, accessToken),
    select: selectFn,
    enabled: args.enabled ?? !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreatePursuitChatSessionBody {
  pursuitId: UUID
  organizationId: UUID
  model_name?: KnownModelName
  title?: string | null
}

export const useCreatePursuitChatSession = () => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (args: CreatePursuitChatSessionBody) =>
      createPursuitChatSession(args, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'pursuit_chat',
          orgId: variables.organizationId,
          sessionId: data.id,
        }),
        data,
      )

      const pursuitChatSessionListKey = chatSessionListQueryKeys.pursuit({
        pursuitId: variables.pursuitId,
      })
      appendChatSessionToQueryData({
        queryClient,
        data,
        key: pursuitChatSessionListKey,
      })

      const simpleChatSessionListItem = chatSessionToListItem(data)
      queryClient.setQueryData(
        simpleChatSessionQueryKeys.session({
          sessionId: data.id,
        }),
        simpleChatSessionListItem,
      )

      // Invalidate the specific agent query
      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'pursuit_chat',
          pursuitId: variables.pursuitId,
        }),
      })
    },
  })
}

interface UseUpdatePursuitChatSessionArgs {
  orgId: UUID
  sessionId: UUID
  pursuitId?: UUID
}

export const useUpdatePursuitChatSession = (
  args: UseUpdatePursuitChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updatePursuitChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'pursuit_chat',
          orgId: args.orgId,
          sessionId: args.sessionId,
        }),
      })

      if (args.pursuitId) {
        queryClient.invalidateQueries({
          queryKey: chatSessionListQueryKeys.pursuit({
            pursuitId: args.pursuitId,
          }),
        })
      }

      // Invalidate the specific agent query
      if (args.pursuitId) {
        return queryClient.invalidateQueries({
          queryKey: chatSessionListQueryKeys.agent({
            agentName: 'pursuit_chat',
            pursuitId: args.pursuitId,
          }),
        })
      }
    },
  })
}

interface UsePursuitChatSessionListArgs {
  orgId: UUID
  pursuitId: UUID
}

export const usePursuitChatSessionList = (
  args: UsePursuitChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.pursuit({
      pursuitId: args.pursuitId,
    }),
    queryFn: () => fetchPursuitChatSessionList(args, accessToken),
    staleTime: Infinity,
  })
}

interface FetchPursuitChatSessionArgs {
  sessionId: UUID | ''
}

const fetchPursuitChatSession = async (
  args: FetchPursuitChatSessionArgs,
  accessToken: string,
): Promise<PursuitChatSession> => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/pursuit/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as PursuitChatSession
}

interface CreatePursuitChatSessionRequest {
  pursuit_id: UUID
  model_name?: KnownModelName
  title?: string | null
}

export const createPursuitChatSession = async (
  args: CreatePursuitChatSessionBody,
  accessToken: string | null | undefined,
) => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/pursuit/sessions`

  const body: CreatePursuitChatSessionRequest = {
    pursuit_id: args.pursuitId,
    model_name: args.model_name ?? DEFAULT_MODEL,
    title: args.title,
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as PursuitChatSession
}

const updatePursuitChatSession = async (
  args: UseUpdatePursuitChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/pursuit/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Pursuit Chats

// region: Pursuit Document Chats

interface UsePursuitDocumentChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  enabled?: boolean
  select?: (data: PursuitDocumentChatSession) => T
}

export const usePursuitDocumentChatSession = <T = PursuitDocumentChatSession>(
  args: UsePursuitDocumentChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PursuitDocumentChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'pursuit_document_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: () => fetchPursuitDocumentChatSession(args, accessToken),
    select: selectFn,
    enabled: args.enabled ?? !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreatePursuitDocumentChatSessionBody {
  pursuitId: UUID
  documentId: UUID
  organizationId: UUID
  model_name?: KnownModelName
  title?: string | null
}

export const useCreatePursuitDocumentChatSession = () => {
  const { accessToken } = useAuthInfo()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (args: CreatePursuitDocumentChatSessionBody) =>
      createPursuitDocumentChatSession(args, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'pursuit_document_chat',
          orgId: variables.organizationId,
          sessionId: data.id,
        }),
        data,
      )

      const pursuitDocumentChatSessionListKey =
        chatSessionListQueryKeys.pursuitDocument({
          pursuitId: variables.pursuitId,
          documentId: variables.documentId,
        })
      appendChatSessionToQueryData({
        queryClient,
        data,
        key: pursuitDocumentChatSessionListKey,
      })

      const simpleChatSessionListItem = chatSessionToListItem(data)
      queryClient.setQueryData(
        simpleChatSessionQueryKeys.session({
          sessionId: data.id,
        }),
        simpleChatSessionListItem,
      )

      // Invalidate the specific agent query
      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'pursuit_document_chat',
          pursuitId: variables.pursuitId,
          documentId: variables.documentId,
        }),
      })
    },
  })
}

interface UseUpdatePursuitDocumentChatSessionArgs {
  orgId: UUID
  sessionId: UUID
  pursuitId?: UUID
  documentId?: UUID
}

export const useUpdatePursuitDocumentChatSession = (
  args: UseUpdatePursuitDocumentChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updatePursuitDocumentChatSession(args, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatSessionQueryKeys.session({
          agentName: 'pursuit_document_chat',
          orgId: args.orgId,
          sessionId: args.sessionId,
        }),
      })

      if (args.pursuitId && args.documentId) {
        queryClient.invalidateQueries({
          queryKey: chatSessionListQueryKeys.pursuitDocument({
            pursuitId: args.pursuitId,
            documentId: args.documentId,
          }),
        })
      }

      // Invalidate the specific agent query
      if (args.pursuitId && args.documentId) {
        return queryClient.invalidateQueries({
          queryKey: chatSessionListQueryKeys.agent({
            agentName: 'pursuit_document_chat',
            pursuitId: args.pursuitId,
            documentId: args.documentId,
          }),
        })
      }
    },
  })
}

interface UsePursuitDocumentChatSessionListArgs {
  orgId: UUID
  pursuitId: UUID
  documentId: UUID
}

export const usePursuitDocumentChatSessionList = (
  args: UsePursuitDocumentChatSessionListArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.pursuitDocument({
      pursuitId: args.pursuitId,
      documentId: args.documentId,
    }),
    queryFn: () => fetchPursuitDocumentChatSessionList(args, accessToken),
    staleTime: Infinity,
  })
}

interface FetchPursuitDocumentChatSessionArgs {
  sessionId: UUID | ''
}

const fetchPursuitDocumentChatSession = async (
  args: FetchPursuitDocumentChatSessionArgs,
  accessToken: string,
): Promise<PursuitDocumentChatSession> => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as PursuitDocumentChatSession
}

interface CreatePursuitDocumentChatSessionRequest {
  pursuit_id: UUID
  document_id: UUID
  model_name?: KnownModelName
  title?: string | null
}

export const createPursuitDocumentChatSession = async (
  args: CreatePursuitDocumentChatSessionBody,
  accessToken: string | null | undefined,
) => {
  if (!accessToken) {
    throw new Error('Access token is required')
  }

  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions`

  const body: CreatePursuitDocumentChatSessionRequest = {
    pursuit_id: args.pursuitId,
    document_id: args.documentId,
    model_name: args.model_name ?? DEFAULT_MODEL,
    title: args.title,
  }

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as PursuitDocumentChatSession
}

const updatePursuitDocumentChatSession = async (
  args: UseUpdatePursuitDocumentChatSessionArgs,
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Pursuit Document Chats

// region: Form Chats

interface UseFormChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  select?: (data: FormChatSession) => T
}

export const useFormChatSession = <T = FormChatSession>(
  args: UseFormChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: FormChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'form_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: () => fetchFormChatSession(args, accessToken),
    select: selectFn,
    enabled: !!args.sessionId,
    staleTime: Infinity,
  })
}

interface CreateFormChatSessionBody {
  formId: UUID
  organizationId: UUID
  model_name?: KnownModelName
  title?: string | null
}

export const useCreateFormChatSession = () => {
  const queryClient = useQueryClient()
  const { accessToken } = useAuthInfoWithThrow()

  return useMutation({
    mutationFn: async (args: CreateFormChatSessionBody) => {
      return createFormChatSession(args, accessToken)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'form_chat',
          orgId: variables.organizationId,
          sessionId: data.id,
        }),
        data,
      )

      const simpleChatSessionListItem = chatSessionToListItem(data)
      queryClient.setQueryData(
        simpleChatSessionQueryKeys.session({ sessionId: data.id }),
        simpleChatSessionListItem,
      )

      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'form_chat',
          orgId: variables.organizationId,
        }),
      })
    },
  })
}

interface UseFormChatSessionListArgs {
  orgId: UUID
}

export const useFormChatSessionList = (args: UseFormChatSessionListArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: chatSessionListQueryKeys.form({ orgId: args.orgId }),
    queryFn: () => fetchFormChatSessionList(args, accessToken),
    staleTime: Infinity,
  })
}

interface FetchFormChatSessionArgs {
  sessionId: UUID | ''
}

const fetchFormChatSession = async (
  args: FetchFormChatSessionArgs,
  accessToken: string,
): Promise<FormChatSession> => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/form/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as FormChatSession
}

const createFormChatSession = async (
  args: CreateFormChatSessionBody,
  accessToken: string,
): Promise<FormChatSession> => {
  const url = `${API_URL}/api/v1/chat/form/sessions`
  const requestBody = {
    form_id: args.formId,
    model_name: args.model_name,
    title: args.title,
  }
  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(requestBody),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as FormChatSession
}

const updateFormChatSession = async (
  args: { organizationId: UUID; sessionId: UUID },
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/form/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }
}

// endregion: Form Chats

// region: Spreadsheet Chats

interface CreateSpreadsheetChatSessionBody {
  spreadsheetId: UUID
  organizationId: UUID
  model_name?: KnownModelName
  title?: string | null
}

export const useCreateSpreadsheetChatSession = () => {
  const queryClient = useQueryClient()
  const { accessToken } = useAuthInfoWithThrow()

  return useMutation({
    mutationFn: async (args: CreateSpreadsheetChatSessionBody) => {
      return createSpreadsheetChatSession(args, accessToken)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        chatSessionQueryKeys.session({
          agentName: 'spreadsheet_chat',
          orgId: variables.organizationId,
          sessionId: data.id,
        }),
        data,
      )

      const simpleChatSessionListItem = chatSessionToListItem(data as any)
      queryClient.setQueryData(
        simpleChatSessionQueryKeys.session({ sessionId: data.id }),
        simpleChatSessionListItem,
      )

      return queryClient.invalidateQueries({
        queryKey: chatSessionListQueryKeys.agent({
          agentName: 'spreadsheet_chat',
          orgId: variables.organizationId,
        }),
      })
    },
  })
}

const createSpreadsheetChatSession = async (
  args: CreateSpreadsheetChatSessionBody,
  accessToken: string,
): Promise<SpreadsheetChatSession> => {
  const url = `${API_URL}/api/v1/chat/spreadsheet/sessions`
  const requestBody = {
    spreadsheet_id: args.spreadsheetId,
    model_name: args.model_name,
    title: args.title,
  }
  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(requestBody),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as SpreadsheetChatSession
}

interface UseSpreadsheetChatSessionArgs<T> {
  orgId: UUID
  sessionId: UUID | ''
  select?: (data: SpreadsheetChatSession) => T
}

export const useSpreadsheetChatSession = <T = SpreadsheetChatSession>(
  args: UseSpreadsheetChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: SpreadsheetChatSession) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: chatSessionQueryKeys.session({
      agentName: 'spreadsheet_chat',
      orgId: args.orgId,
      sessionId: args.sessionId,
    }),
    queryFn: () => fetchSpreadsheetChatSession(args, accessToken),
    select: selectFn,
    enabled: !!args.sessionId,
    staleTime: Infinity,
  })
}

interface FetchSpreadsheetChatSessionArgs {
  sessionId: UUID | ''
}

const fetchSpreadsheetChatSession = async (
  args: FetchSpreadsheetChatSessionArgs,
  accessToken: string,
): Promise<SpreadsheetChatSession> => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/spreadsheet/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
    })
  }

  return (await res.json()) as SpreadsheetChatSession
}

// endregion: Spreadsheet Chats

// endregion: Pursuit Chats

// region: Simple Chats

interface SimpleChatSessionQueryKeyArgs {
  sessionId: UUID | ''
}

export const simpleChatSessionQueryKeys = {
  session: (args: SimpleChatSessionQueryKeyArgs) =>
    ['chat-session', 'simple', args.sessionId] as const,
}

interface UseSimpleChatSessionArgs<T> {
  sessionId: UUID | ''
  select?: (data: ChatSessionListItem) => T
}

export const useSimpleChatSession = <T = ChatSessionListItem>(
  args: UseSimpleChatSessionArgs<T>,
) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: ChatSessionListItem) => {
      if (args.select) {
        return args.select(data)
      }
      return data as unknown as T
    },
    [args.select],
  )

  return useQuery({
    queryKey: simpleChatSessionQueryKeys.session(args),
    queryFn: () => fetchSimpleChatSession(args, accessToken),
    staleTime: Infinity,
    enabled: !!args.sessionId,
    select: selectFn,
  })
}

interface UseChatSessionAuthorArgs {
  sessionId: UUID | undefined
}

const selectChatSessionAuthor = (data: ChatSessionListItem) => {
  return data.created_by
}

export const useChatSessionAuthor = (args: UseChatSessionAuthorArgs) => {
  return useSimpleChatSession({
    sessionId: args.sessionId ?? '',
    select: selectChatSessionAuthor,
  })
}

interface UseChatSessionIsSharedArgs {
  sessionId: UUID | undefined
}

export const useChatSessionIsShared = (args: UseChatSessionIsSharedArgs) => {
  const { user } = useAuthInfo()

  const selectChatSessionIsShared = useCallback(
    (data: ChatSessionListItem) => {
      if (user === undefined || user === null) {
        return false
      }
      return data.created_by !== user.userId
    },
    [user?.userId],
  )

  return useSimpleChatSession({
    sessionId: args.sessionId ?? '',
    select: selectChatSessionIsShared,
  })
}

interface FetchSimpleChatSessionArgs {
  sessionId: UUID | ''
}

const fetchSimpleChatSession = async (
  args: FetchSimpleChatSessionArgs,
  accessToken: string,
) => {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const url = `${API_URL}/api/v1/chat/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }

  return (await res.json()) as ChatSessionListItem
}

export interface UseUpdateSimpleChatSessionArgs {
  sessionId: UUID
  queryKeys?: ChatSessionListQueryKeyArgs[]
}

export const useUpdateSimpleChatSession = (
  args: UseUpdateSimpleChatSessionArgs,
) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: ChatSessionUpdate) =>
      updateSimpleChatSession({ sessionId: args.sessionId }, req, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: simpleChatSessionQueryKeys.session({
          sessionId: args.sessionId,
        }),
      })

      return _.isEmpty(args.queryKeys)
        ? queryClient.invalidateQueries({
            queryKey: chatSessionListQueryKeys.all(),
            exact: false,
          })
        : Promise.all(
            _.chain(args.queryKeys)
              .map(chatSessionListQueryKeys.agent)
              .map((queryKey) => queryClient.invalidateQueries({ queryKey }))
              .value(),
          )
    },
  })
}

const updateSimpleChatSession = async (
  args: { sessionId: UUID },
  req: ChatSessionUpdate,
  accessToken: string,
) => {
  const url = `${API_URL}/api/v1/chat/sessions/${args.sessionId}`

  const res: Response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

// endregion: Simple Chats

// region: User Chats

export interface UseChatSessionsArgs {
  queryKeys: ChatSessionListQueryKeyArgs[]
  select?: (data: ChatSessionListItem[]) => ChatSessionListItem[]
}

const combineChatSessionListResponses = (
  responses: UseQueryResult<ChatSessionListItem[], Error>[],
) => {
  return {
    data: responses
      .flatMap((response) => response.data)
      .filter((session) => session !== undefined),
    isLoading: responses.some((response) => response.isLoading),
    isPending: responses.some((response) => response.isPending),
    isFetching: responses.some((response) => response.isFetching),
    isFetched: responses.every((response) => response.isFetched),
    isError: responses.some((response) => response.isError),
    isSuccess: responses.every((response) => response.isSuccess),
  }
}

const selectChatSessionList = (data: ChatSessionListResponse) => {
  return data.sessions
}

export const useChatSessions = (args: UseChatSessionsArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  const combineFn = useCallback(
    (responses: UseQueryResult<ChatSessionListItem[], Error>[]) => {
      const combined = combineChatSessionListResponses(responses)
      return {
        ...combined,
        data: args.select ? args.select(combined.data) : combined.data,
        count: combined.data.length,
        has_more: false,
      }
    },
    [args.select],
  )

  const queries = args.queryKeys.map((queryKey) => ({
    queryKey: chatSessionListQueryKeys.agent(queryKey),
    queryFn: async () => {
      const data = await fetchChatSessionList(queryKey, accessToken)

      // Cache individual sessions
      for (const session of data.sessions) {
        queryClient.setQueryData(
          simpleChatSessionQueryKeys.session({ sessionId: session.id }),
          session,
        )
      }

      return data
    },
    staleTime: Infinity,
    select: selectChatSessionList,
  }))

  return useQueries({ queries, combine: combineFn })
}

// endregion: User Chats

// region: Bulk Operations

export interface UseBulkChatSessionsDeleteArgs {
  queryKeys: ChatSessionListQueryKeyArgs[]
}

export const useDeleteChatSessions = (args: UseBulkChatSessionsDeleteArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: BulkChatSessionsDelete) =>
      bulkDeleteChatSessions(req, accessToken),
    onSuccess: () => {
      // Invalidate all affected agent queries
      const promises = args.queryKeys.map((queryKey) =>
        queryClient.invalidateQueries({
          queryKey: chatSessionListQueryKeys.agent(queryKey),
        }),
      )

      return Promise.all(promises)
    },
  })
}

const bulkDeleteChatSessions = async (
  req: BulkChatSessionsDelete,
  accessToken: string,
) => {
  if (req.ids.length === 0) {
    return
  }

  const url = `${API_URL}/api/v1/chat/sessions/bulk`

  const res: Response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
}

// endregion: Bulk Operations

// region: Duplication Operations

interface DuplicateChatSessionArgs {
  sessionId: UUID
}

export interface UseDuplicateChatSessionArgs {
  queryKeys: ChatSessionListQueryKeyArgs[]
}

export const useDuplicateChatSession = (args: UseDuplicateChatSessionArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: DuplicateChatSessionArgs) =>
      duplicateChatSession(args.sessionId, accessToken),
    onSuccess: (data, variables) => {
      // Add to agent-specific list
      for (const queryKey of args.queryKeys) {
        if (data.initial_agent_name !== queryKey.agentName) {
          // avoids duplicates
          continue
        }
        queryClient.setQueryData<ChatSessionListResponse>(
          chatSessionListQueryKeys.agent(queryKey),
          produce((draft) => {
            if (!draft) {
              return {
                sessions: [data],
                count: 1,
                has_more: false,
              }
            }
            draft.sessions.unshift(data) // Add to beginning of list
            draft.count += 1
          }),
        )
      }
    },
  })
}

const duplicateChatSession = async (
  sessionId: UUID,
  accessToken: string,
): Promise<ChatSessionListItem> => {
  const url = `${API_URL}/api/v1/chat/sessions/${sessionId}/duplicate`

  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return (await res.json()) as ChatSessionListItem
}

// endregion: Duplication Operations

// region: Utils

interface AppendChatSessionToQueryDataArgs {
  queryClient: QueryClient
  data:
    | OrganizationChatSession
    | OpportunityChatSession
    | PursuitChatSession
    | PursuitDocumentChatSession
    | FormChatSession
  key: QueryKey
}

const appendChatSessionToQueryData = (
  args: AppendChatSessionToQueryDataArgs,
) => {
  const { queryClient, data, key } = args
  queryClient.setQueryData<ChatSessionListResponse>(
    key,
    produce((draft) => {
      const newSession = {
        id: data.id,
        created_at: data.created_at,
        created_by: data.created_by_user_id,
        last_modified: data.last_modified,
        title: data.title,
        initial_agent_name: data.initial_agent_name,
        shared_with_user_ids: data.shared_with_user_ids,
      }

      if (!draft) {
        // don't update the query data if the query data hasn't been fetched yet
        return undefined
      }

      draft.sessions.push(newSession)
      draft.count += 1
    }),
  )
}

const chatSessionToListItem = (
  data:
    | OrganizationChatSession
    | OpportunityChatSession
    | PursuitChatSession
    | PursuitDocumentChatSession
    | FormChatSession,
): ChatSessionListItem => {
  return {
    id: data.id,
    created_at: data.created_at,
    created_by: data.created_by_user_id,
    last_modified: data.last_modified,
    initial_agent_name: data.initial_agent_name,
    shared_with_user_ids: data.shared_with_user_ids,
    title: data.title,
  }
}

const invalidateKeysOnSuccess = (
  args: { invalidateKeysOnSuccess?: QueryKey[] },
  queryClient: QueryClient,
): Promise<void> | undefined => {
  if (args.invalidateKeysOnSuccess) {
    for (const [index, key] of args.invalidateKeysOnSuccess.entries()) {
      const promise = queryClient.invalidateQueries({ queryKey: key })
      if (index === args.invalidateKeysOnSuccess.length - 1) {
        return promise
      }
    }
  }
}

// endregion: Utils
